import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Create axios instance with credentials for httpOnly cookies
    const api = axios.create({
        baseURL: `${API_URL}/api`,
        headers: {
            'Content-Type': 'application/json',
        },
        withCredentials: true, // Send cookies with every request
    });

    // Add response interceptor for token refresh
    api.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;
            
            // If token expired and we haven't tried to refresh yet
            if (error.response?.status === 401 && 
                error.response?.data?.detail === 'Token expired' &&
                !originalRequest._retry) {
                originalRequest._retry = true;
                
                try {
                    // Try to refresh token
                    await api.post('/auth/refresh');
                    // Retry the original request
                    return api(originalRequest);
                } catch (refreshError) {
                    // Refresh failed, logout user
                    setUser(null);
                    return Promise.reject(refreshError);
                }
            }
            return Promise.reject(error);
        }
    );

    const fetchUser = useCallback(async () => {
        try {
            const response = await api.get('/auth/me');
            setUser(response.data);
        } catch (error) {
            // User not authenticated or token invalid
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const login = async (email, password) => {
        try {
            const response = await api.post('/auth/login', { email, password });
            const { user: userData } = response.data;
            localStorage.removeItem('quality_popup_dismissed');
            localStorage.removeItem('eeoc_nudge_dismissed');
            await api.post('/focus-guard/session-start').catch(() => {});
            setUser(userData);
            return { success: true, user: userData };
        } catch (error) {
            console.error('Login failed:', error);
            return { 
                success: false, 
                error: error.response?.data?.detail || 'Login failed' 
            };
        }
    };

    const register = async (name, email, password, role = 'user') => {
        try {
            const response = await api.post('/auth/register', { name, email, password, role });
            const { user: userData } = response.data;
            setUser(userData);
            return { success: true, user: userData };
        } catch (error) {
            console.error('Registration failed:', error);
            return { 
                success: false, 
                error: error.response?.data?.detail || 'Registration failed' 
            };
        }
    };

    const logout = async () => {
        try {
            await api.post('/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('quality_popup_dismissed');
            localStorage.removeItem('eeoc_nudge_dismissed');
            setUser(null);
        }
    };

    const loginWithToken = async (providedToken) => {
        try {
            // If token is provided, use Authorization header (backward compat)
            // Otherwise, rely on httpOnly cookies already set by backend
            const config = {
                withCredentials: true
            };
            
            if (providedToken) {
                config.headers = { Authorization: `Bearer ${providedToken}` };
            }
            
            const response = await axios.get(`${API_URL}/api/auth/me`, config);
            await axios.post(`${API_URL}/api/focus-guard/session-start`, null, config).catch(() => {});
            sessionStorage.removeItem('focus_guard_signed_out');
            
            setUser(response.data);
            return { success: true, user: response.data };
        } catch (error) {
            console.error('Token login failed:', error);
            throw new Error(error.response?.data?.detail || 'Token authentication failed');
        }
    };

    const updateUser = (updates) => {
        setUser(prev => ({ ...prev, ...updates }));
    };

    // Refresh user data from server
    const refreshUser = async () => {
        try {
            const response = await api.get('/auth/me');
            setUser(response.data);
            return response.data;
        } catch (error) {
            console.error('Failed to refresh user:', error);
            return null;
        }
    };

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            login,
            loginWithToken,
            register,
            logout,
            updateUser,
            refreshUser,
            api,
            isAuthenticated: !!user,
            isAdmin: user?.role === 'admin',
            isTrainer: user?.role === 'trainer',
            isUser: user?.role === 'user',
            isTestUser: user?.is_test_user || false,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
